import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "../App";
import { Logo } from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle2, XCircle, Award, ShieldCheck, ExternalLink } from "lucide-react";

const VerifyCertification = () => {
  const { verificationId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/verify/${verificationId}`);
        if (res.ok) setData(await res.json());
        else setData({ valid: false, reason: "Could not verify" });
      } catch {
        setData({ valid: false, reason: "Network error" });
      }
      setLoading(false);
    };
    load();
  }, [verificationId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A1A4A] via-[#1E3A8A] to-[#0F1B3D] flex items-center justify-center p-6">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#FFD507]/10 rounded-full blur-3xl pointer-events-none" />
      <div className="relative max-w-xl w-full">
        <div className="text-center mb-6">
          <Logo variant="dark" height={56} />
          <p className="text-[#FFD507] text-xs uppercase tracking-[0.2em] font-semibold mt-3">Credential Verification</p>
        </div>
        <Card className="bg-white shadow-2xl border-0">
          <CardContent className="p-8 text-center" data-testid="verify-card">
            {loading ? (
              <p className="text-slate-500">Verifying…</p>
            ) : data?.valid ? (
              <>
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-12 h-12 text-emerald-600" />
                </div>
                <p className="text-emerald-700 font-bold uppercase tracking-wider text-sm">Verified Credential</p>
                <h1 className="text-3xl font-heading font-bold text-slate-900 mt-3">{data.recipient_name}</h1>
                <p className="text-slate-500 text-sm mt-1">has successfully completed</p>
                <p className="text-xl font-heading font-bold text-[#1E3A8A] mt-2">{data.course_name}</p>
                <div className="flex items-center justify-center gap-2 mt-4">
                  <div className="w-12 h-12 rounded-full bg-[#FFD507] flex items-center justify-center">
                    <ShieldCheck className="w-6 h-6 text-[#1E3A8A]" />
                  </div>
                  <p className="text-sm text-slate-700 font-semibold">Aspiritech Verified</p>
                </div>
                <div className="mt-6 pt-4 border-t border-slate-100 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-slate-400 uppercase text-xs">Issued</p>
                    <p className="font-semibold text-slate-700">{new Date(data.issued_at).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 uppercase text-xs">By</p>
                    <p className="font-semibold text-slate-700">{data.issued_by}</p>
                  </div>
                </div>
                <div className="mt-4 text-xs text-slate-400 break-all">
                  Verification ID: <span className="font-mono">{data.verification_id}</span>
                </div>
              </>
            ) : (
              <>
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-rose-100 flex items-center justify-center">
                  <XCircle className="w-12 h-12 text-rose-600" />
                </div>
                <p className="text-rose-700 font-bold uppercase tracking-wider text-sm">Not Verified</p>
                <h1 className="text-2xl font-heading font-bold text-slate-900 mt-3">Credential not found</h1>
                <p className="text-slate-500 text-sm mt-2">{data?.reason || "The verification ID is invalid."}</p>
              </>
            )}
            <a
              href="https://aspiritech.org"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-[#1E3A8A] hover:underline mt-6"
            >
              Learn about Aspiritech Academy <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </CardContent>
        </Card>
        <p className="text-center text-xs text-slate-400 mt-4">
          Aspiritech Academy issues digital credentials with cryptographic verification IDs.
        </p>
      </div>
    </div>
  );
};

export default VerifyCertification;
